/**
 * Users Page — Admin Console
 * Matches PaaS UsersListTab structure exactly.
 * Stats: inline dots. Table: checkbox, user, status, credentials, roles, groups, actions.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Plus, Search, Pencil, KeyRound, Ban, Trash2, Download,
  Copy, Eye, EyeOff, X, FilterX, ChevronDown, RefreshCw,
  UserCheck, UserX, Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth-context';
import { useHasPermission } from '@/lib/use-has-permission';
// B2c-T1 fix (review R-03) — the per-row bulk contract, from its one
// declaration in the Users data layer rather than a second copy here.
import { summarizeBulkRows, type BulkRowResult } from '@/lib/use-users';

/** Past tense for the toast, per bulk action the menu offers. */
const BULK_VERB: Record<string, string> = {
  activate: 'Activated',
  deactivate: 'Deactivated',
  delete: 'Deleted',
};

export const Route = createFileRoute('/admin/users')({ component: AccessControlPage });

interface User {
  id: string; email: string; firstName: string | null; lastName: string | null;
  phoneNumber?: string | null; avatarUrl?: string | null; isActive: boolean | null;
  status: string | null; isEmailVerified?: boolean | null; mfaEnabled?: boolean;
  role: string | null; lastLoginAt: string | null; createdAt: string;
}
// B2c-T1 fix (review R-05 / BF-05) — `inactive` was the v0 value c31 removed
// from the alphabet, and `userStatsSchema` renamed the field to `deactivated`
// (packages/api/src/routes/v1/users.ts). Reading the old key here computed
// `undefined + n` and the DISABLED tile rendered "NaN".
interface Stats { total: number; active: number; deactivated: number; suspended: number; mfaEnabled: number; }

function initials(u: User) { return ((u.firstName?.charAt(0) ?? '') + (u.lastName?.charAt(0) ?? '')).toUpperCase() || u.email.charAt(0).toUpperCase(); }
function fullName(u: User) { return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email; }

function AccessControlPage() {
  const navigate = useNavigate();
  // B1-T3: was `useAuth().canAdmin`, a `roleLevel >= admin` ladder read.
  // `write:users` is the key the Admin bundle holds and Member, Power User
  // and Viewer do not, and it is what every /users write route and the
  // cross-user reads gate on (SEC-B1-F3) — the same line, as a grant.
  const { isLoading: authLoading } = useAuth();
  const canAdmin = useHasPermission('write', 'users');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, active: 0, deactivated: 0, suspended: 0, mfaEnabled: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [visiblePins, setVisiblePins] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, sr] = await Promise.all([
        apiClient.get<{ data: User[]; pagination: { totalCount: number } }>(`/users?pageSize=50&search=${searchTerm}${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`),
        apiClient.get<Stats>('/users/stats'),
      ]);
      setUsers(ur.data ?? []); setStats(sr);
    } catch { /* empty */ }
    setLoading(false);
  }, [searchTerm, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSelectAll = (checked: boolean) => setSelectedIds(checked ? new Set(users.map(u => u.id)) : new Set());
  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedIds(prev => { const n = new Set(prev); checked ? n.add(id) : n.delete(id); return n; });
  };
  const copyToClipboard = (text: string, label: string) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  // B2c-T1 fix (review R-03 / BF-05) — REPORT WHAT THE SERVER DID, per row.
  // `POST /users/bulk` answers `{ success, results: [{ id, outcome, reason? }] }`
  // and refuses a row the caller may not act on, is in a state with no edge for
  // the action, or — for a deactivation — has no reason. This used to ignore
  // `results` entirely and toast `"${action} completed"` unconditionally, so a
  // bulk deactivate in which every single row was refused told the operator it
  // had worked while the accounts stayed live.
  const handleBulk = async (action: string, reason?: string) => {
    const ids = Array.from(selectedIds);
    try {
      const res = await apiClient.post<{ success: boolean; results: BulkRowResult[] }>(
        '/users/bulk',
        { action, userIds: ids, ...(reason ? { reason } : {}) },
      );
      // The severity and the sentence come from the ONE reader the tenant
      // Users page uses, so the two rosters cannot report the same batch
      // differently.
      const { severity, message } = summarizeBulkRows(
        res.results ?? [],
        ids.length,
        BULK_VERB[action] ?? action,
      );
      toast[severity](message);
      setSelectedIds(new Set());
      load();
    } catch { toast.error('Failed'); }
  };
  const handleResetPassword = async (id: string) => {
    try { const r = await apiClient.post<{ temporaryPassword: string }>(`/users/${id}/reset-password`, {}); toast.success(`Password: ${r.temporaryPassword}`); } catch { toast.error('Failed'); }
  };
  // B2c-T1 fix (review R-02 / BF-05) — SEND THE REASON THE TABLE ASKS FOR.
  // `POST /:id/toggle-status` reads `requiresReason` off the resolved edge, and
  // both edges into `deactivated` declare it
  // (packages/shared/src/schemas/user-lifecycle.ts), so posting `{}` answered
  // 400 and the Disable control was dead. The direction is the SERVER's
  // decision — this page does not re-derive it — so the reason is collected for
  // either direction and the endpoint records it or ignores it as the edge says.
  const handleToggleStatus = async (id: string, reason: string) => {
    try { await apiClient.post(`/users/${id}/toggle-status`, { reason }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Failed'); }
  };

  // The one reason prompt, for the two controls whose edges require one. It
  // reuses the AlertDialog + Input this page already renders; no new layout.
  const [reasonFor, setReasonFor] = useState<{ kind: 'toggle'; id: string } | { kind: 'bulk'; action: string } | null>(null);
  const [reasonText, setReasonText] = useState('');
  const submitReason = async () => {
    const target = reasonFor;
    const reason = reasonText.trim();
    if (!target || !reason) return;
    setReasonFor(null); setReasonText('');
    if (target.kind === 'toggle') await handleToggleStatus(target.id, reason);
    else await handleBulk(target.action, reason);
  };
  const handleDelete = async () => {
    if (!deleteId) return;
    try { await apiClient.delete(`/users/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); } catch { toast.error('Failed'); }
  };

  const handleExport = (format: 'csv' | 'xlsx') => {
    const rows = users.map(u => ({
      Email: u.email,
      'First Name': u.firstName ?? '',
      'Last Name': u.lastName ?? '',
      Status: u.status ?? '',
      Role: u.role ?? '',
      'MFA Enabled': u.mfaEnabled ? 'Yes' : 'No',
      'Last Login': u.lastLoginAt ? new Date(u.lastLoginAt).toISOString() : 'Never',
      'Created': new Date(u.createdAt).toISOString(),
    }));
    if (rows.length === 0) { toast.error('No users to export'); return; }

    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(','),
      ...rows.map(r => headers.map(h => {
        const val = String(r[h as keyof typeof r] ?? '');
        return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
      }).join(',')),
    ];
    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: format === 'csv' ? 'text/csv;charset=utf-8;' : 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().slice(0, 10)}.${format === 'csv' ? 'csv' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} users as ${format.toUpperCase()}`);
  };

  // Permission guard — only admins can access user management
  if (!authLoading && !canAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Users className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="text-sm text-muted-foreground mt-1">You need admin privileges to manage users.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/' })}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Access Control</h1>
          <p className="text-sm text-muted-foreground">Manage users, groups, and roles for this organization.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" />Invite User</Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Users ({stats.total})</TabsTrigger>
          <TabsTrigger value="groups">User Groups</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-3 space-y-3">
          {/* ── Stats bar + filters (PaaS exact layout) ─────────────── */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-2 bg-muted/20 border rounded-lg">
            {/* Stats dots */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-bold uppercase tracking-tight text-muted-foreground px-2">
              <div className="flex items-center gap-1.5"><div className="h-1.5 w-1.5 rounded-full bg-blue-600" /> TOTAL: <span className="text-foreground">{stats.total}</span></div>
              <div className="flex items-center gap-1.5"><div className="h-1.5 w-1.5 rounded-full bg-green-500" /> ACTIVE: <span className="text-foreground">{stats.active}</span></div>
              <div className="flex items-center gap-1.5"><div className="h-1.5 w-1.5 rounded-full bg-blue-400" /> INVITED: <span className="text-foreground">0</span></div>
              <div className="flex items-center gap-1.5"><div className="h-1.5 w-1.5 rounded-full bg-red-500" /> DISABLED: <span className="text-foreground">{stats.deactivated + stats.suspended}</span></div>
            </div>

            {/* Filters / bulk actions */}
            <div className="flex flex-wrap items-center gap-2 px-1">
              {selectedIds.size > 0 ? (
                <div className="flex items-center gap-2 animate-in fade-in zoom-in duration-200">
                  <span className="text-xs font-bold text-primary mr-2 shrink-0">{selectedIds.size} SELECTED</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="h-7 text-[10px] font-bold gap-1">Bulk Actions <ChevronDown className="h-3 w-3" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Multi-User Management</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleBulk('activate')} className="text-xs"><UserCheck className="mr-2 h-3.5 w-3.5 text-green-600" /> Activate Accounts</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setReasonText(''); setReasonFor({ kind: 'bulk', action: 'deactivate' }); }} className="text-xs"><UserX className="mr-2 h-3.5 w-3.5 text-amber-600" /> Disable Accounts</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleBulk('delete')} className="text-xs text-destructive"><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Accounts</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 w-7 p-0"><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); }}>
                    <SelectTrigger className="w-full sm:w-[130px] h-8 text-xs font-medium"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {/* B2c-T1 fix (review R-04 / BF-05) — the states the
                          lifecycle alphabet actually holds. `inactive` is the
                          v0 value c31 removed, and `GET /users?status=` now
                          validates against `LifecycleStateSchema`, so picking
                          "Disabled" here answered 400 and the page's empty
                          `catch` rendered an empty roster with no error. Same
                          set the tenant Users page offers
                          (routes/users/index.tsx). */}
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="invited">Invited</SelectItem>
                      <SelectItem value="locked">Locked</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-8 gap-2 text-xs font-medium"><Download className="h-3.5 w-3.5" /> Export</Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40"><DropdownMenuItem className="text-xs" onClick={() => handleExport('csv')}>CSV</DropdownMenuItem><DropdownMenuItem className="text-xs" onClick={() => handleExport('xlsx')}>XLSX</DropdownMenuItem></DropdownMenuContent>
                  </DropdownMenu>
                  <div className="relative flex items-center">
                    {showSearch ? (
                      <div className="flex items-center gap-1 animate-in slide-in-from-right-4 duration-200">
                        <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" /><Input autoFocus placeholder="Search..." className="pl-7 h-8 w-[120px] sm:w-[150px] text-xs" value={searchTerm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} /></div>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setShowSearch(false); setSearchTerm(''); }} aria-label="Close search" title="Close search"><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setShowSearch(true)} aria-label="Search users" title="Search users"><Search className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>Search Users</TooltipContent></Tooltip>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── User Table (PaaS structure) ──────────────────────────── */}
          <Card className="shadow-sm border-muted/60 overflow-hidden">
            <CardContent className="p-0 overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
              ) : (
                <Table className="min-w-[900px]">
                  <TableHeader className="bg-muted/30">
                    <TableRow className="h-10">
                      <TableHead className="w-[40px] pl-4"><Checkbox checked={selectedIds.size === users.length && users.length > 0} onCheckedChange={(c) => handleSelectAll(!!c)} /></TableHead>
                      <TableHead className="w-[250px]">User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Credentials</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>User Groups</TableHead>
                      <TableHead className="text-right pr-4">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length > 0 ? users.map(user => (
                      <TableRow key={user.id} className="cursor-pointer hover:bg-muted/10 group h-[72px]" onClick={() => navigate({ to: '/users/$userId', params: { userId: user.id } })}>
                        <TableCell className="pl-4" onClick={e => e.stopPropagation()}><Checkbox checked={selectedIds.has(user.id)} onCheckedChange={(c) => handleSelectRow(user.id, !!c)} /></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8 shrink-0"><AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{initials(user)}</AvatarFallback></Avatar>
                            <div className="flex flex-col overflow-hidden">
                              <span className="font-semibold text-sm group-hover:text-primary truncate">{fullName(user)}</span>
                              <div className="flex items-center gap-1.5 overflow-hidden">
                                <span className="text-sm text-muted-foreground truncate font-medium">{user.email}</span>
                                <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0 hover:text-primary" onClick={e => { e.stopPropagation(); copyToClipboard(user.email, 'Email'); }} aria-label="Copy email" title="Copy email"><Copy className="h-2.5 w-2.5" /></Button>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.status === 'active' ? 'default' : 'secondary'} className={cn('text-[10px] h-5 px-1.5', user.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200')}>
                            {user.status === 'active' ? 'Active' : user.status === 'deactivated' ? 'Deactivated' : (user.status ?? 'Active')}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex flex-col gap-1 text-[10px]">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground w-8">PIN#</span>
                              <span className="font-mono font-medium">{visiblePins[user.id] ? '1234' : '••••'}</span>
                              <Button variant="ghost" size="icon" className="h-4 w-4" onClick={() => setVisiblePins(p => ({ ...p, [user.id]: !p[user.id] }))} aria-label={visiblePins[user.id] ? 'Hide PIN' : 'Show PIN'} title={visiblePins[user.id] ? 'Hide PIN' : 'Show PIN'}>{visiblePins[user.id] ? <EyeOff className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5" />}</Button>
                            </div>
                            <div className="flex items-center gap-2"><span className="text-muted-foreground w-8">RFID</span><span className="font-mono font-medium">-</span></div>
                            <div className="flex items-center gap-2"><span className="text-muted-foreground w-8">CODE</span><span className="font-mono font-medium">-</span></div>
                          </div>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-4 rounded-full bg-green-500" />
                            <Badge variant="outline" className="text-[9px] px-1 py-0 font-bold text-green-700 border-green-400">DIRECT</Badge>
                            <span className="text-[10px] font-bold text-muted-foreground capitalize">{user.role === 'power_user' ? 'Power User' : user.role ?? 'Member'}</span>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">0</Badge></TableCell>
                        <TableCell className="text-right pr-4" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end items-center gap-0.5">
                            <TooltipProvider delayDuration={0}>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => navigate({ to: '/users/$userId', params: { userId: user.id } })} aria-label={`Edit user '${fullName(user)}'`} title="Edit details"><Pencil className="h-3.5 w-3.5" /></Button></TooltipTrigger><TooltipContent>Edit Details</TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => handleResetPassword(user.id)} aria-label={`Reset password for '${fullName(user)}'`} title="Reset password"><KeyRound className="h-3.5 w-3.5" /></Button></TooltipTrigger><TooltipContent>Reset Password</TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className={cn('h-7 w-7', user.status === 'active' ? 'text-muted-foreground hover:text-amber-600' : 'text-green-600')} onClick={() => { setReasonText(''); setReasonFor({ kind: 'toggle', id: user.id }); }} aria-label={user.status === 'active' ? `Disable user '${fullName(user)}'` : `Activate user '${fullName(user)}'`} title={user.status === 'active' ? 'Disable' : 'Activate'}><Ban className="h-3.5 w-3.5" /></Button></TooltipTrigger><TooltipContent>{user.status === 'active' ? 'Disable' : 'Activate'}</TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteId(user.id)} aria-label={`Delete user '${fullName(user)}'`} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button></TooltipTrigger><TooltipContent>Delete</TooltipContent></Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={7} className="h-48 text-center text-muted-foreground">No users found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <Card><CardContent className="pt-6 flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground max-w-md">Create user groups, manage their members, and assign group-level roles.</p>
            <Button onClick={() => navigate({ to: '/users/groups' })}>Open group management</Button>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="roles" className="mt-4">
          <Card><CardContent className="pt-6 flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground max-w-md">View the tenant's roles and their privileges, and create or edit custom roles.</p>
            <Button onClick={() => navigate({ to: '/users/roles' })}>Open role management</Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <CreateUserDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load} />
      {/* B2c-T1 fix (review R-02 / R-03) — the reason the transition table asks
          for. Reuses the AlertDialog + Input already on this page. */}
      <AlertDialog open={!!reasonFor} onOpenChange={(o) => { if (!o) { setReasonFor(null); setReasonText(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Why are you making this change?</AlertDialogTitle>
            <AlertDialogDescription>
              Deactivating an account revokes every session and starts its recovery window. The reason is recorded on the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="lifecycle-reason">Reason</Label>
            <Input
              id="lifecycle-reason"
              autoFocus
              value={reasonText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReasonText(e.target.value)}
              placeholder="e.g. left the company on 12 Sep"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!reasonText.trim()} onClick={submitReason}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete User?</AlertDialogTitle><AlertDialogDescription>This will permanently deactivate this account.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: () => void }) {
  const [email, setEmail] = useState(''); const [firstName, setFirstName] = useState(''); const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState(''); const [role, setRole] = useState('member'); const [creating, setCreating] = useState(false);
  const handleCreate = async () => {
    if (!email || !password) { toast.error('Email and password required'); return; }
    setCreating(true);
    try { await apiClient.post('/users', { email, password, firstName: firstName || undefined, lastName: lastName || undefined, role }); toast.success('User created'); onOpenChange(false); setEmail(''); setFirstName(''); setLastName(''); setPassword(''); setRole('member'); onCreated(); } catch { toast.error('Failed'); }
    setCreating(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label className="text-xs">First Name</Label><Input value={firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)} /></div><div className="space-y-1.5"><Label className="text-xs">Last Name</Label><Input value={lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)} /></div></div>
        <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Password</Label><Input type="password" value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Role</Label><Select value={role} onValueChange={setRole}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="member">Member</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent></Select></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create'}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
